import React from 'react';
import { X } from 'lucide-react';
import type { Creator } from '../../../types/creator';

interface CreatorDetailsProps {
  creator: Creator;
  onClose: () => void;
}

export function CreatorDetails({ creator, onClose }: CreatorDetailsProps) {
  const formatDate = (date: Date | null) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
      <div className="w-full max-w-2xl bg-gray-800 h-full overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 z-10 border-b border-gray-700">
          <div className="flex justify-between items-center p-6">
            <h2 className="text-xl font-bold text-gray-100">Creator Details</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Basic Information */}
          <section aria-labelledby="basic-info">
            <h3 id="basic-info" className="text-lg font-semibold text-gray-100 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="block text-sm font-medium text-gray-400">Name</span>
                <div className="mt-1 text-gray-100">
                  {`${creator.firstName} ${creator.lastName || ''}`}
                </div>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-400">Email</span>
                <div className="mt-1 text-gray-100">{creator.email}</div>
              </div>
            </div>
          </section>

          {/* Contract Details */}
          <section aria-labelledby="contract-info">
            <h3 id="contract-info" className="text-lg font-semibold text-gray-100 mb-4">Contract Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="block text-sm font-medium text-gray-400">Retainer Amount</span>
                <div className="mt-1 text-gray-100">
                  ${creator.retainerAmount.toLocaleString()}
                </div>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-400">Commission</span>
                <div className="mt-1 text-gray-100">{creator.commission}%</div>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-400">Posts per Month</span>
                <div className="mt-1 text-gray-100">{creator.postsPerMonth}</div>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-400">Onboarding Completed</span>
                <div className="mt-1 text-gray-100">{formatDate(creator.onboardingCompletedAt)}</div>
              </div>
            </div>
          </section>

          {/* Address */}
          <section aria-labelledby="address-info">
            <h3 id="address-info" className="text-lg font-semibold text-gray-100 mb-4">Address</h3>
            <div className="space-y-2">
              <div>
                <span className="block text-sm font-medium text-gray-400">Street Address</span>
                <div className="mt-1 text-gray-100">
                  {creator.addressLine1}
                  {creator.addressLine2 && <div>{creator.addressLine2}</div>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <span className="block text-sm font-medium text-gray-400">City</span>
                  <div className="mt-1 text-gray-100">{creator.city}</div>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-400">State</span>
                  <div className="mt-1 text-gray-100">{creator.state}</div>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-400">ZIP Code</span>
                  <div className="mt-1 text-gray-100">{creator.zipCode}</div>
                </div>
              </div>
            </div>
          </section>

          {/* Social Media */}
          <section aria-labelledby="social-media">
            <h3 id="social-media" className="text-lg font-semibold text-gray-100 mb-4">Social Media</h3>
            <div className="flex gap-6">
              {creator.instagramHandle && (
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-400 mb-1">Instagram</span>
                  <a
                    href={`https://instagram.com/${creator.instagramHandle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    @{creator.instagramHandle}
                  </a>
                </div>
              )}
              {creator.tiktokHandle && (
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-400 mb-1">TikTok</span>
                  <a
                    href={`https://tiktok.com/@${creator.tiktokHandle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    @{creator.tiktokHandle}
                  </a>
                </div>
              )}
              {creator.youtubeHandle && (
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-gray-400 mb-1">YouTube</span>
                  <a
                    href={`https://youtube.com/@${creator.youtubeHandle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:text-indigo-300"
                  >
                    @{creator.youtubeHandle}
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* Payment Information */}
          <section aria-labelledby="payment-info">
            <h3 id="payment-info" className="text-lg font-semibold text-gray-100 mb-4">Payment Information</h3>
            
            {/* Bank Account */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-300 mb-3">Bank Account</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-sm font-medium text-gray-400">Account Holder Name</span>
                  <div className="mt-1 text-gray-100">
                    {creator.bankFirstName && creator.bankLastName 
                      ? `${creator.bankFirstName} ${creator.bankLastName}`
                      : 'Not provided'}
                  </div>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-400">Account Type</span>
                  <div className="mt-1 text-gray-100">{creator.bankAccountType || 'Not provided'}</div>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-400">Account Number</span>
                  <div className="mt-1 text-gray-100">
                    {creator.bankAccountNumber 
                      ? `${creator.bankAccountNumber}`
                      : 'Not provided'}
                  </div>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-400">Routing Number</span>
                  <div className="mt-1 text-gray-100">
                    {creator.bankRoutingNumber 
                      ? `${creator.bankRoutingNumber}`
                      : 'Not provided'}
                  </div>
                </div>
              </div>
            </div>

            {/* Alternative Payment Methods */}
            <div>
              <h4 className="text-md font-medium text-gray-300 mb-3">Alternative Payment Methods</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="block text-sm font-medium text-gray-400">Venmo</span>
                  <div className="mt-1 text-gray-100">{creator.venmoHandle || 'Not provided'}</div>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-400">Zelle Email</span>
                  <div className="mt-1 text-gray-100">{creator.zelleEmail || 'Not provided'}</div>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-400">Zelle Phone</span>
                  <div className="mt-1 text-gray-100">{creator.zellePhone || 'Not provided'}</div>
                </div>
              </div>
            </div>
          </section>

          {/* Metadata */}
          <section aria-labelledby="metadata">
            <h3 id="metadata" className="text-lg font-semibold text-gray-100 mb-4">Metadata</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="block text-sm font-medium text-gray-400">Created At</span>
                <div className="mt-1 text-gray-100">{formatDate(creator.createdAt)}</div>
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-400">Last Updated</span>
                <div className="mt-1 text-gray-100">{formatDate(creator.updatedAt)}</div>
              </div>
              {creator.deletedAt && (
                <div>
                  <span className="block text-sm font-medium text-gray-400">Deleted At</span>
                  <div className="mt-1 text-gray-100">{formatDate(creator.deletedAt)}</div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
} 